<template>
    <App title="Allocate Inventory">
        <div class="allocation-wizard">

            <!-- Page Header -->
            <div class="wizard-header mb-4">
                <div class="d-flex align-items-center gap-3">
                    <h4 class="wizard-title mb-0">Inventory Allocation</h4>
                    <span class="wizard-badge">
                        <i class="fa fa-boxes-stacked"></i>
                        Step {{ currentStep }} of {{ steps.length }}
                    </span>
                </div>
                <p class="wizard-subtitle mt-1">{{ stepDescriptions[currentStep - 1] }}</p>
            </div>

            <!-- Stepper -->
            <div class="stepper-track mb-4">
                <template v-for="(step, i) in steps" :key="i">
                    <div class="stepper-step" :class="{
                        'is-active': currentStep === i + 1,
                        'is-done': currentStep > i + 1,
                    }">
                        <div class="stepper-circle">
                            <i v-if="currentStep > i + 1" class="fa fa-check"></i>
                            <span v-else>{{ i + 1 }}</span>
                        </div>
                        <span class="stepper-label">{{ step }}</span>
                    </div>
                    <div v-if="i < steps.length - 1" class="stepper-connector" :class="{ 'is-done': currentStep > i + 1 }"></div>
                </template>
            </div>

            <!-- ─── STEP 1: Details ─── -->
            <transition name="fade-slide" mode="out-in">
                <div v-if="currentStep === 1" key="step1">
                    <div class="step-card mb-3">
                        <div class="step-card-header">
                            <div class="step-icon-wrap">
                                <i class="fa fa-warehouse"></i>
                            </div>
                            <div>
                                <h6 class="step-card-title">Source &amp; Destination</h6>
                                <p class="step-card-sub">Choose where items come from and where they go</p>
                            </div>
                        </div>
                        <div class="row g-3 mt-1">
                            <div class="col-md-4">
                                <label class="field-label">Warehouse</label>
                                <SelectField id="warehouse" v-model="warehouse" name="warehouse" rules="required" label="Warehouse" :options="warehouse_options" class="form-select form-select-sm" />
                            </div>
                            <div class="col-md-4">
                                <label class="field-label">Destination Station</label>
                                <SelectField key="station-select" v-model="station" id="location" name="location" rules="required" label="Pick a Station" :options="station_options" class="form-select form-select-sm" />
                            </div>
                            <div class="col-md-4">
                                <label class="field-label">Station Type</label>
                                <SelectField id="type" v-model="station_type" name="type" label="Station Type" class="form-select form-select-sm" :options = "type_options">
                                    <option v-for="s in stations" :key="s.name" :value="s.value">{{ s.value }}</option>
                                </SelectField>
                            </div>
                        </div>
                    </div>

                    <div class="step-card mb-3">
                        <div class="step-card-header">
                            <div class="step-icon-wrap">
                                <i class="fa fa-user-shield"></i>
                            </div>
                            <div>
                                <h6 class="step-card-title">Authority</h6>
                                <p class="step-card-sub">Allocation will be recorded under this administrator</p>
                            </div>
                        </div>
                        <div class="row g-3 mt-1">
                            <div class="col-md-4">
                                <label class="field-label">Allocated By</label>
                                <InputField id="admin_name" v-model="admin_name" name="admin_name" rules="required" label="Admin Name" placeholder="Administrator name" disabled class="form-control form-control-sm" />
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- ─── STEP 2: Allocate ─── -->
            <transition name="fade-slide" mode="out-in">
                <div v-if="currentStep === 2" key="step2">
                    <div class="summary-bar mb-3">
                        <div class="summary-chip">
                            <i class="fa fa-warehouse"></i>
                            {{ warehouseName || 'Warehouse' }}
                        </div>
                        <i class="fa fa-arrow-right summary-arrow"></i>
                        <div class="summary-chip">
                            <i class="fa fa-location-dot"></i>
                            {{ stationName || 'Station' }}
                        </div>
                        <div v-if="selectedItems.length > 0" class="ms-auto summary-chip accent">
                            <i class="fa fa-boxes-stacked"></i>
                            {{ selectedItems.length }} item{{ selectedItems.length !== 1 ? 's' : '' }} selected
                        </div>
                    </div>

                    <DataGrid ref="inventoryGrid" :url="route('stock.ajax.items')" title="Select Inventory Items" item-key="id" display-field="serial" :selected-items="selectedItems" @update:selectedItems="selectedItems = $event"
                        @itemAction="handleItemAction" @dataLoaded="handleDataLoaded" @error="handleGridError" :filters="inventoryFilters" :additional-params="additionalParams" :auto-load="canLoadInventory"
                        search-placeholder="Search by serial number or type..." loading-text="Loading inventory items..." empty-state-title="No inventory items found" empty-state-message="Try adjusting your search criteria or filters"
                        :default-items-per-page="20" :search-debounce="500">
                        <template #filters="{ resetPagination, refresh }">
                            <div class="col-md-3">
                                <label class="field-label">Filter by Type</label>
                                <select v-model="inventoryFilters.type" @change="resetPagination(); refresh()" class="form-select form-select-sm">
                                    <option value="">All Types</option>
                                    <option v-for="inventory in inventorys" :key="inventory.value" :value="inventory.value">
                                        {{ inventory.name }}
                                    </option>
                                </select>
                            </div>
                        </template>
                    </DataGrid>
                </div>
            </transition>

            <!-- ─── STEP 3: Review ─── -->
            <transition name="fade-slide" mode="out-in">
                <div v-if="currentStep === 3" key="step3">

                    <div class="receipt-card mb-3">

                        <!-- top accent stripe -->
                        <div class="receipt-stripe"></div>

                        <!-- ── Header ── -->
                        <div class="row g-0 px-4 pt-4 pb-3 align-items-start">
                            <!-- Left: brand + title -->
                            <div class="col">
                                <div class="d-flex align-items-center gap-2 mb-2">
                                    <div class="receipt-brand-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zm-9-4h2v4h-2V3z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="receipt-brand-name">Warehouse</p>
                                        <p class="receipt-brand-sub">Management System</p>
                                    </div>
                                </div>
                                <h1 class="receipt-title">Allocation Review</h1>
                                <p class="receipt-ref">Pending Reference</p>
                            </div>
                            <!-- Right: status -->
                            <div class="col-auto text-end">
                                <span class="receipt-status-pill s-pending">
                                    <span class="receipt-status-dot"></span>
                                    Pending
                                </span>
                                <div class="receipt-date-meta mt-2">
                                    <div>Prepared by &nbsp;<strong>{{ admin_name }}</strong></div>
                                </div>
                            </div>
                        </div>

                        <!-- ── Inventory summary ── -->
                        <div class="px-4 pb-4">
                            <div class="receipt-summary-box">
                                <div class="d-flex align-items-center gap-3 pb-3 mb-3 border-bottom">
                                    <span class="receipt-qty">{{ selectedItemDetails.length }}</span>
                                    <div class="vr flex-shrink-0" style="height:38px;opacity:.2"></div>
                                    <div>
                                        <p class="receipt-qty-label">Total Inventory</p>
                                        <p class="receipt-qty-sub">Stock items to be allocated</p>
                                    </div>
                                </div>
                                <p class="receipt-type-heading">By type</p>
                                <div class="d-flex flex-wrap gap-1">
                                    <span v-for="entry in selectedItemsByType" :key="entry.type" class="receipt-type-pill">
                                        <span class="receipt-type-dot"></span>
                                        <span class="receipt-type-name">{{ entry.type }}</span>
                                        <span class="receipt-type-count">{{ entry.count }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- ── Meta grid ── -->
                        <div class="row g-0 px-4 py-1 border-top border-bottom">
                            <div class="col-6 col-md-4 py-2">
                                <p class="receipt-meta-label">From Warehouse</p>
                                <p class="receipt-meta-value">{{ warehouseName || '—' }}</p>
                            </div>
                            <div class="col-6 col-md-4 py-2">
                                <p class="receipt-meta-label">To Station</p>
                                <p class="receipt-meta-value">{{ stationName || '—' }}</p>
                            </div>
                            <div class="col-6 col-md-4 py-2">
                                <p class="receipt-meta-label">Station Type</p>
                                <p class="receipt-meta-value">{{ station_type || '—' }}</p>
                            </div>
                            <div class="col-6 col-md-4 py-2">
                                <p class="receipt-meta-label">Assigned By</p>
                                <p class="receipt-meta-value">{{ admin_name }}</p>
                            </div>
                            <div class="col-6 col-md-4 py-2">
                                <p class="receipt-meta-label">Status</p>
                                <p class="receipt-meta-value">Pending Approval</p>
                            </div>
                        </div>

                        <!-- ── Stock items table ── -->
                        <StockTable :items="selectedItemDetails" :filter-types="selectedItemsByType.map(e => e.type)" :loading="false" :per-page="10" />

                        <!-- ── Footer ── -->
                        <div class="d-flex justify-content-between align-items-end px-4 py-3 border-top">
                            <div>
                                <div class="receipt-sig-line mb-1"></div>
                                <p class="receipt-sig-name">{{ admin_name }}</p>
                                <p class="receipt-sig-role">Allocated By</p>
                            </div>
                            <div class="text-end receipt-footer-meta">
                                <div>To: <strong class="text-dark">{{ stationName }}</strong></div>
                                <div>Warehouse Management System</div>
                            </div>
                        </div>

                    </div>
                </div>
            </transition>

            <!-- Navigation -->
            <div class="wizard-nav mt-4">
                <button class="nav-btn nav-btn-ghost" @click="handleBack">
                    <i class="fa fa-arrow-left"></i>
                    {{ currentStep === 1 ? 'Cancel' : 'Back' }}
                </button>
                <div class="d-flex gap-2 align-items-center">
                    <span v-if="!canProceed && currentStep < 3" class="nav-hint">
                        {{ currentStep === 1 ? 'Complete all fields to continue' : 'Select at least one item to continue' }}
                    </span>
                    <button v-if="currentStep < 3" class="nav-btn nav-btn-primary" @click="nextStep" :disabled="!canProceed">
                        Continue
                        <i class="fa fa-arrow-right"></i>
                    </button>
                    <button v-if="currentStep === 3" class="nav-btn nav-btn-primary" @click="submitAllocation" :disabled="!canSubmit">
                        <i class="fa fa-check"></i>
                        Allocate Items
                        <span class="count-pill">{{ selectedItems.length }}</span>
                    </button>
                </div>
            </div>

        </div>

        <!-- Notifications -->
        <div class="mt-9 position-absolute top-0 end-0">
            <NotivueKeyboard v-slot="{ containersTabIndex }">
                <Notivue v-slot="item" :containersTabIndex="containersTabIndex">
                    <AppNotification :item="item" />
                </Notivue>
            </NotivueKeyboard>
        </div>
    </App>
</template>

<script lang="ts">
import { useConfirm } from '@/libs/useConfirm.ts'
import { error } from '@/ts/app'
import Alert from '@/vue/components/Alert.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import DataGrid from '@/vue/components/DataGrid.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import SidebarEffect from '@/vue/components/SidebarEffect.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import StockTable from '@/widget/table/StockTable.vue'
import { usePage } from '@inertiajs/vue3'
import $ from 'jquery'
import { Notification, NotificationProgress, Notivue, NotivueKeyboard, push } from 'notivue'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, toNative, Vue, Watch } from 'vue-facing-decorator'

interface InventoryItem {
    id: number
    serial: string
    type: string
    warehouse: string
    created_at?: string
}

@Component({
    components: {
        App,
        Form,
        Alert,
        InputField,
        PhoneField,
        Button,
        Block,
        SelectField,
        TextareaField,
        SidebarEffect,
        Notivue,
        Notification,
        NotificationProgress,
        AppNotification,
        NotivueKeyboard,
        DataGrid,
        StockTable
    },
})

class Create extends Vue {
    page = usePage()

    currentStep = 1
    steps = ['Details', 'Allocate', 'Review']
    stepDescriptions = [
        'Select warehouse, station type and destination',
        'Choose the inventory items to allocate',
        'Review your selection and confirm the allocation',
    ]

    station = ''
    stationName: string = ''

    warehouse: string | number = ''
    warehouseName: string = ''
    station_type = ''
    admin_name = this.page.props.auth.user.firstname + ' ' + this.page.props.auth.user.lastname
    admin = this.page.props.auth.user.id
    selectedInventoryType = ''
    searchQuery = ''
    selectedItems: number[] = []
    selectedItemDetails: InventoryItem[] = []
    itemsPerPage: number = 20
    currentPage = 1
    inventoryItems: InventoryItem[] = []
    isLoading = false
    inventoryFilters = {
        type: '',
    }
    filterOptions = {
        types: [] as string[],
    }
    message = ''
    done = false
    confirm = useConfirm().confirm

    get payload() {
        return {
            warehouse: this.warehouse,
            station: this.station,
            admin: this.admin,
            stocks: this.selectedItems,
        }
    }

    get canProceed(): boolean {
        if (this.currentStep === 1) {
            return !!this.warehouse && !!this.station_type && !!this.station
        }
        if (this.currentStep === 2) {
            return this.selectedItems.length > 0
        }
        return true
    }

    nextStep() {
        if (this.currentStep < 3 && this.canProceed) {
            this.currentStep++
        }
    }

    handleBack() {
        if (this.currentStep > 1) {
            this.currentStep--
        } else {
            window.history.back()
        }
    }

    @Ref('inventoryGrid')
    readonly inventoryGrid!: any

    @Prop({ type: Array })
    readonly stations!: Array<{ name: string; value: string }>

    @Prop({ type: Array })
    readonly inventorys!: Array<{ name: string; value: string }>

    get canLoadInventory(): boolean {
        return !!this.warehouse
    }

    get selectedItemsByType(): { type: string; count: number }[] {
        const map: Record<string, number> = {}
        for (const item of this.selectedItemDetails) {
            const key = item.type ?? 'Unknown'
            map[key] = (map[key] ?? 0) + 1
        }
        return Object.entries(map)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count)
    }

    get canSubmit(): boolean {
        return this.selectedItems.length > 0 && !!this.warehouse && !!this.station
    }

    get additionalParams() {
        const params: Record<string, any> = {}
        if (this.warehouse) {
            params.warehouse = this.warehouse
        }
        return params
    }

    handleItemAction(item: InventoryItem) {
        this.viewItemDetails(item)
    }

    handleDataLoaded(data: any) {
        const items: InventoryItem[] = data.items || []
        this.inventoryItems = items
        // Accumulate details for selected items as pages load
        for (const item of items) {
            if (this.selectedItems.includes(item.id) && !this.selectedItemDetails.find(d => d.id === item.id)) {
                this.selectedItemDetails.push(item)
            }
        }
    }

    @Watch('selectedItems')
    onSelectedItemsChanged(newIds: number[]) {
        // Remove items that were deselected
        this.selectedItemDetails = this.selectedItemDetails.filter(d => newIds.includes(d.id))
        // Add newly selected items that are on the currently loaded page
        for (const item of this.inventoryItems) {
            if (newIds.includes(item.id) && !this.selectedItemDetails.find(d => d.id === item.id)) {
                this.selectedItemDetails.push(item)
            }
        }
    }

    handleGridError(err: any) {
        console.error('Grid error:', err)
        push.error({ message: 'Failed to load inventory items' })
    }

    viewItemDetails(item: InventoryItem) {
        Swal.fire({
            title: 'Item Details',
            html: `
				<div class="text-start">
					<p><strong>Serial:</strong> ${item.serial}</p>
					<p><strong>Type:</strong> ${item.type}</p>
				</div>
			`,
            confirmButtonText: 'Close'
        })
    }

    get type_options(): Select2Options {
		return {
			placeholder: 'Select Station Type',
			minimumResultsForSearch: Infinity,
			//@ts-ignore
			ajax: null,
			
		}
	}

    get station_options(): Select2Options {
        const stationUrl = this.route('station.ajax.select.station')
        //const clusterUrl = this.route('cluster.ajax.select.cluster')
        return {
            placeholder: 'Pick a Station',
            minimumResultsForSearch: 3,
            ajax: {
                method:'get',
                url: stationUrl,
            },
            //@ts-ignore
            templateResult: ({ id, title, text, type }: any) => {
                if (!id) return text
                const badge = type === 'franchise'
                    ? `<small class="ms-1 text-info">(Franchise)</small>`
                    : `<small class="ms-1 text-muted">(Swap)</small>`
                return $(`<span>${title}${badge}</span>`)
            },
            //@ts-ignore
            templateSelection: ({ id, title, text }: any) => (id ? $(`<span>${title}</span>`) : text),
        }
    }

    get warehouse_options(): Select2Options {
        return {
            placeholder: 'Select Warehouse',
            minimumResultsForSearch: 3,
            ajax: {
                method: 'get',
                url: this.route('warehouse.ajax.select.warehouse'),
            },
            templateResult: ({ id, title, text }: any) => (id ? $(`<span>${title}</span>`) : text),
            templateSelection: ({ id, title, text }: any) => (id ? $(`<span>${title}</span>`) : text),
            data: [{
                id: this.$user.workplace?.slug ?? this.$user.workplace?.id ?? '',
                text: this.$user.workplace?.title,
                title: this.$user.workplace?.title,
                selected: true
            }]
        }
    }

    get inventoryType_options(): Select2Options {
        return {
            placeholder: 'Select Inventory Type',
            minimumResultsForSearch: Infinity,
            ajax: null,
        }
    }

    @Watch('warehouse')
    onWarehouseChanged(newWarehouse: string | number) {
        this.selectedItems = []
        this.inventoryItems = []

        this.$nextTick(() => {
            const $el = $('#warehouse') as any
            if ($el.select2) {
                const selected = $el.select2('data')
                if (selected && selected[0] && selected[0].id) {
                    this.warehouseName = selected[0].title || selected[0].text || String(newWarehouse)
                }
            }
            if (newWarehouse && this.inventoryGrid) {
                this.inventoryGrid.refresh()
            }
        })
    }

    @Watch('station')
    onStationChanged(newStation: string) {
        if (!newStation) {
            this.stationName = ''
            this.station_type = ''
            return
        }
        this.$nextTick(() => {
            const $el = $('#location') as any
            if ($el.select2) {
                const selected = $el.select2('data')
                if (selected && selected[0] && selected[0].id) {
                    this.stationName = selected[0].title || selected[0].text || String(newStation)
                    // select2 preserves the full result object — type is on it
                    this.station_type = selected[0].type === 'franchise' ? 'franchise' : 'swap'
                }
            }
        })
    }



    mounted() {
		const wp = this.$user.workplace
    if (wp) {
        this.warehouse = wp.slug ?? wp.id ?? ''
        this.warehouseName = wp.title ?? ''
    }
        this.loadFilterOptions()
        this.$nextTick(() => {
            $('#location').on('select2:select', (e: any) => {
                const data = e.params?.data
                if (data) {
                    this.stationName = data.title || data.text || ''
                    this.station_type = data.type === 'franchise' ? 'franchise' : 'swap'

                    // Force the disabled select2 to display the new value
                    this.$nextTick(() => {
                        const $typeEl = $('#type') as any
                        if ($typeEl.select2) {
                            $typeEl.val(this.station_type).trigger('change')
                        }
                    })
                }
            })

            $('#location').on('select2:clear', () => {
                this.stationName = ''
                this.station_type = ''
                this.$nextTick(() => {
                    const $typeEl = $('#type') as any
                    if ($typeEl.select2) {
                        $typeEl.val('').trigger('change')
                    }
                })
            })
        })
    }

    async loadFilterOptions() {
        try {
            const response = await this.$http.get(this.route('stock.ajax.filters'))
            this.filterOptions = response.data
        } catch (err) {
            console.error('Failed to load filter options:', err)
        }
    }

    async loadWarehouseItems() {
        try {
            const response = await this.$http.get(this.route('stock.ajax.items'))
            this.inventoryItems = response.data
        } catch (err) {
            console.error('Failed to load warehouse items:', err)
        }
    }

    submitAllocation() {
        if (this.selectedItems.length === 0) {
            push.error({ message: 'Please select at least one inventory item' })
            return
        }

        try {
            const payload = {
                ...this.payload,
                station_type: this.station_type
            }
            this.$http
                .post(this.route('assignment.ajax.create'), payload)
                .then((response) => {
                    this.done = true
                    this.message = response?.data?.message || 'Inventory created successfully.'
                    Swal.fire({
                        title: 'Success!',
                        text: this.message,
                        icon: 'success',
                        confirmButtonText: 'OK',
                    }).then(() => {
                        this.router.visit(this.route('batch.page.group'))
                        this.message = ''
                        Swal.close()
                    })
                })
                .catch((e) => {
                    this.message = e.response?.data?.message || 'An error occurred.'
                    push.error({
                        title: 'Error',
                        message: this.message
                    })
                })
        } catch (e: any) {
            error(e)
        }
    }

    async confirmAction() {
        const isConfirmed = await this.confirm(
            'Are you sure you want to cancel this operation?',
            {
                title: 'Cancel Process',
                confirmText: 'Yes, Cancel',
                cancelText: 'No, Continue',
            }
        )

        if (!isConfirmed) return

        setTimeout(() => {
            this.router.visit(this.route('batch.page.group'))
        }, 300)
    }
}

export default toNative(Create)
</script>

<style lang="scss" scoped>
$primary: #3D5A00;
$primary-light: #f0f4e6;
$primary-hover: #4a6e00;
$border: #e5e7eb;
$text-dark: #111827;
$text-mid: #374151;
$text-light: #9ca3af;
$radius-lg: 14px;
$radius-md: 10px;
$radius-sm: 8px;
$shadow-base: 0 1px 3px rgba(0, 0, 0, 0.05);
$shadow-hover: 0 4px 16px rgba(0, 0, 0, 0.08);

/* ── Wizard Wrapper ── */
.allocation-wizard {
    max-width: 860px;
    margin: 0 auto;
}

/* ── Header ── */
.wizard-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: $text-dark;
    letter-spacing: -0.3px;
}

.wizard-subtitle {
    font-size: 0.8rem;
    color: $text-light;
    margin: 0;
}

.wizard-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: $primary-light;
    color: $primary;
    border-radius: 20px;
    padding: 0.2rem 0.75rem;
    font-size: 0.7rem;
    font-weight: 600;
}

/* ── Stepper ── */
.stepper-track {
    display: flex;
    align-items: flex-start;
}

.stepper-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    &.is-done {
        .stepper-circle {
            background: $primary;
            border-color: $primary;
            color: #fff;
        }

        .stepper-label {
            color: $primary;
        }
    }

    &.is-active {
        .stepper-circle {
            background: #fff;
            border-color: $primary;
            color: $primary;
            box-shadow: 0 0 0 4px $primary-light;
        }

        .stepper-label {
            color: $primary;
            font-weight: 600;
        }
    }
}

.stepper-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid $border;
    background: #fff;
    color: $text-light;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.82rem;
    font-weight: 700;
    transition: all 0.25s ease;
}

.stepper-label {
    font-size: 0.72rem;
    font-weight: 500;
    color: $text-light;
    white-space: nowrap;
    transition: color 0.25s ease;
}

.stepper-connector {
    flex: 1;
    height: 2px;
    background: $border;
    margin-top: 17px;
    margin-left: 4px;
    margin-right: 4px;
    border-radius: 2px;
    transition: background 0.3s ease;

    &.is-done {
        background: $primary;
    }
}

/* ── Step Cards ── */
.step-card {
    background: #fff;
    border: 1px solid $border;
    border-radius: $radius-lg;
    padding: 1.5rem;
    box-shadow: $shadow-base;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: $shadow-hover;
    }
}

.step-card-header {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
}

.step-icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: $primary-light;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;

    &.success {
        background: #d1fae5;
        color: #065f46;
    }
}

.step-card-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: $text-dark;
    margin: 0 0 0.15rem;
}

.step-card-sub {
    font-size: 0.78rem;
    color: $text-light;
    margin: 0;
}

/* ── Field Labels ── */
.field-label {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: $text-light;
    text-transform: uppercase;
    margin-bottom: 0.4rem;
}

/* ── Form controls ── */
:deep(.form-control),
:deep(.form-select) {
    border-color: $border;
    border-radius: $radius-sm;
    font-size: 0.85rem;
    color: $text-mid;

    &:focus {
        border-color: $primary;
        box-shadow: 0 0 0 3px rgba(61, 90, 0, 0.08);
    }

    &:disabled {
        background: #f9fafb;
        color: $text-light;
    }
}

/* ── Receipt card (Step 3) ── */
.receipt-card {
    background: #fff;
    border: 1px solid #e8e7e2;
    border-radius: 0;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.receipt-stripe {
    height: 4px;
    background: #0d0e12;
}

.receipt-brand-icon {
    width: 30px;
    height: 30px;
    background: #0d0e12;
    color: #fff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.receipt-brand-name {
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1;
    margin: 0;
}

.receipt-brand-sub {
    font-size: 10px;
    color: #9396a4;
    letter-spacing: 0.04em;
    line-height: 1;
    margin: 0.2rem 0 0;
}

.receipt-title {
    font-size: 2rem;
    font-weight: 900;
    color: #0d0e12;
    margin: 0.5rem 0 0.25rem;
    line-height: 1.1;
}

.receipt-ref {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #9396a4;
    margin: 0;
}

.receipt-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 11px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;

    &.s-pending {
        background: #fdf3db;
        color: #8a5c00;
    }

    &.s-approved {
        background: #e8f5ef;
        color: #1a6644;
    }

    &.s-declined {
        background: #fdeaea;
        color: #8a1f1f;
    }
}

.receipt-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
}

.receipt-date-meta {
    font-size: 11px;
    color: #9396a4;
    line-height: 1.7;
    text-align: right;
}

.receipt-summary-box {
    border: 1px solid #e8e7e2;
    border-radius: 8px;
    padding: 1.25rem;
    background: #fff;
}

.receipt-qty {
    font-size: 52px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: -2px;
    color: #0d0e12;
    flex-shrink: 0;
}

.receipt-qty-label {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #0d0e12;
    margin: 0;
}

.receipt-qty-sub {
    font-size: 10px;
    color: #9396a4;
    margin: 0;
}

.receipt-type-heading {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 500;
    color: #9396a4;
    margin: 0 0 0.5rem;
}

.receipt-type-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 5px 12px;
    background: var(--bs-secondary-bg, #f8f9fa);
}

.receipt-type-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #9ca3af;
    flex-shrink: 0;
    opacity: 0.5;
}

.receipt-type-name {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7280;
}

.receipt-type-count {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.3px;
    color: #0d0e12;
}

.receipt-meta-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #9396a4;
    margin: 0 0 3px;
}

.receipt-meta-value {
    font-size: 12.5px;
    font-weight: 500;
    color: #0d0e12;
    margin: 0;
}

.receipt-sig-line {
    width: 110px;
    border-top: 1px solid #0d0e12;
}

.receipt-sig-name {
    font-size: 12.5px;
    font-weight: 500;
    color: #0d0e12;
    margin: 0;
}

.receipt-sig-role {
    font-size: 11px;
    color: #9396a4;
    margin: 0;
}

.receipt-footer-meta {
    font-size: 11px;
    color: #9396a4;
    line-height: 1.7;
}

/* ── Summary Bar (Step 2) ── */
.summary-bar {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    background: #fff;
    border: 1px solid $border;
    border-radius: $radius-md;
    padding: 0.65rem 1rem;
    box-shadow: $shadow-base;
    flex-wrap: wrap;
}

.summary-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.78rem;
    font-weight: 500;
    color: $text-mid;
    background: #f9fafb;
    border: 1px solid $border;
    border-radius: 6px;
    padding: 0.25rem 0.7rem;

    i {
        color: $text-light;
        font-size: 0.72rem;
    }

    &.accent {
        background: $primary-light;
        border-color: darken(#f0f4e6, 6%);
        color: $primary;

        i {
            color: $primary;
        }
    }
}

.summary-arrow {
    color: $text-light;
    font-size: 0.72rem;
}

/* ── Navigation Bar ── */
.wizard-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border: 1px solid $border;
    border-radius: $radius-lg;
    padding: 1rem 1.25rem;
    box-shadow: $shadow-base;
}

.nav-hint {
    font-size: 0.75rem;
    color: $text-light;
}

.nav-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    height: 38px;
    padding: 0 1.2rem;
    border-radius: $radius-sm;
    font-size: 0.83rem;
    font-weight: 500;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
}

.nav-btn-ghost {
    background: #fff;
    border-color: $border;
    color: $text-mid;

    &:hover:not(:disabled) {
        background: #f9fafb;
        border-color: #d1d5db;
    }
}

.nav-btn-primary {
    background: $primary;
    border-color: $primary;
    color: #fff;

    &:hover:not(:disabled) {
        background: $primary-hover;
        border-color: $primary-hover;
    }
}

.count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    font-size: 0.68rem;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
}

/* ── Transitions ── */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
